/**
 * InstructorProfileView
 * Displays a single instructor's profile: personal details, slot preferences,
 * and credentialed courses.
 *
 * Used in Impersonation Mode so admins can see exactly what data drives the
 * scheduler for a given instructor.
 */

import { useState, useEffect } from 'react';
import {
  schedulerApi,
  InstructorData,
  SlotPreferenceData,
  SlotInfo,
  CredentialData,
} from '../../services/scheduler-api';

const CARD = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: '12px',
  padding: '1.5rem',
} as const;

const PREF_META: Record<string, { label: string; color: string; bg: string }> = {
  '1': { label: 'Preferred',   color: '#4ade80', bg: 'rgba(74,222,128,0.12)' },
  '2': { label: 'Available',   color: '#facc15', bg: 'rgba(250,204,21,0.12)' },
  x:   { label: 'Unavailable', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  '':  { label: 'Not set',     color: 'var(--text-secondary)', bg: 'transparent' },
};

function fmtTime(t: string): string {
  if (!t) return '';
  const s = t.toString().padStart(4, '0');
  const h = parseInt(s.slice(0, 2), 10);
  const m = s.slice(2);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

interface Props {
  asEmail: string;
}

export function InstructorProfileView({ asEmail }: Props) {
  const [instructor, setInstructor] = useState<InstructorData | null>(null);
  const [preferences, setPreferences] = useState<SlotPreferenceData[]>([]);
  const [slots, setSlots] = useState<SlotInfo[]>([]);
  const [credentials, setCredentials] = useState<CredentialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const emailLower = asEmail.toLowerCase().trim();

    Promise.all([
      schedulerApi.getInstructors(),
      schedulerApi.getSlotPreferences(),
      schedulerApi.getCredentials(),
    ])
      .then(([instrData, slotData, credData]) => {
        const found = instrData.instructors.find(
          i => i.email.toLowerCase() === emailLower
        ) ?? null;
        setInstructor(found);

        // Filter to this instructor's row_key
        if (found) {
          setPreferences(
            slotData.preferences.filter(p => p.instructor_row_key === found.row_key)
          );
          setCredentials(
            credData.credentials.filter(c => c.instructor_row_key === found.row_key)
          );
        }
        setSlots(slotData.slots);
      })
      .catch(e => setError(e?.response?.data?.error || e?.message || 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, [asEmail]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
        Loading profile…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...CARD, color: '#f87171', textAlign: 'center', padding: '2rem' }}>
        ❌ {error}
      </div>
    );
  }

  if (!instructor) {
    return (
      <div style={{ ...CARD, textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
        Instructor record not found for <strong>{asEmail}</strong>
      </div>
    );
  }

  // Build slot preference lookup: slot_row_key → preference
  const prefBySlot: Record<string, string> = {};
  preferences.forEach(p => { prefBySlot[p.slot_row_key] = p.preference; });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ── Personal Information ── */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '16px', color: 'var(--text-primary)' }}>
          👤 Personal Information
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1.25rem' }}>
          {[
            { label: 'Full Name',     value: instructor.name },
            { label: 'Email',         value: instructor.email },
            { label: 'Faculty Type',  value: instructor.faculty_type },
            {
              label: 'Do Not Schedule',
              value: instructor.do_not_schedule ? '🚫 Yes' : '✅ No',
              valueColor: instructor.do_not_schedule ? '#f87171' : '#4ade80',
            },
          ].map(item => (
            <div key={item.label}>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                {item.label}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: (item as any).valueColor ?? 'var(--text-primary)' }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {instructor.scheduling_notes && (
          <div style={{ marginTop: '1.25rem', padding: '0.875rem 1rem', background: 'rgba(250,204,21,0.06)', border: '1px solid rgba(250,204,21,0.2)', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#facc15', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
              Scheduling Notes
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              {instructor.scheduling_notes}
            </div>
          </div>
        )}
      </div>

      {/* ── Slot Preferences ── */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '16px', color: 'var(--text-primary)' }}>
          🕐 Time Slot Preferences
        </h3>
        {slots.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No slot data available.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
            {slots.map(slot => {
              const pref = prefBySlot[slot.row_key] ?? '';
              const meta = PREF_META[pref] ?? PREF_META[''];
              return (
                <div
                  key={slot.row_key}
                  style={{
                    padding: '1rem',
                    borderRadius: '10px',
                    background: meta.bg,
                    border: `1px solid ${pref ? meta.color + '40' : 'var(--card-border)'}`,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Slot {slot.row_key}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                    {fmtTime(slot.begin)}<br />
                    <span style={{ fontWeight: '400', fontSize: '11px', color: 'var(--text-secondary)' }}>
                      – {fmtTime(slot.end)}
                    </span>
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: meta.color,
                    background: pref ? meta.color + '20' : 'var(--bg-secondary)',
                  }}>
                    {pref === '1' ? '⭐ Preferred'
                      : pref === '2' ? '✅ Available'
                      : pref === 'x' ? '🚫 Unavailable'
                      : '— Not set'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Credentialed Courses ── */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '16px', color: 'var(--text-primary)' }}>
          📚 Credentialed Courses
          <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: '400', color: 'var(--text-secondary)' }}>
            ({credentials.length} course{credentials.length !== 1 ? 's' : ''})
          </span>
        </h3>
        {credentials.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            No credentials on file — this instructor will not be assigned any sections.
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {credentials
              .sort((a, b) => a.course_code.localeCompare(b.course_code))
              .map(c => (
                <span
                  key={c.row_key}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '20px',
                    background: 'rgba(var(--accent-rgb, 255,215,0),0.1)',
                    border: '1px solid rgba(var(--accent-rgb, 255,215,0),0.25)',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--accent)',
                    letterSpacing: '0.03em',
                  }}
                >
                  {c.course_code.toUpperCase()}
                </span>
              ))}
          </div>
        )}
      </div>

    </div>
  );
}
